export type SortingOrder = 'ASC' | 'DESC';
export type SongStatus =
  | 'NEW'
  | 'AWAITING_GENERATION'
  | 'GENERATING'
  | 'READY'
  | 'FAILED';
/**
 * Statuses shown in the user's song list: everything except NEW (not yet
 * queued) and FAILED. AWAITING_GENERATION / GENERATING are included so a song
 * stays visible while an additional generation is in progress.
 */
export const VISIBLE_SONG_STATUSES: readonly SongStatus[] = [
  'AWAITING_GENERATION',
  'GENERATING',
  'READY',
];
export type UserRole = 'USER' | 'ADMIN';
export type Gender = 'male' | 'female' | 'duet';
export type PaymentMethod = 'YOOKASSA';
/** Balance buckets exposed by user profile responses. */
export type BalanceType =
  | 'GENERATION'
  | 'GENERATION_VOICE'
  | 'TRACK_UNLOCK'
  | 'ADDITIONAL_GENERATION';
export type HttpMethod = 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';

export interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
}

export interface Violation {
  message: string;
  detail: Record<string, unknown> | null;
}

export interface Violations extends Record<string, unknown> {
  violations: Violation[];
}

export interface ErrorResponse {
  message: string;
  trace_id: string | null;
  detail: Record<string, unknown> | null;
}

export interface SuccessResponse {
  success: true;
}

export interface UserProfile {
  id: string;
  name: string;
  login: string;
  role: UserRole;
  balances: Partial<Record<BalanceType, number>>;
  savedSongsCount: number;
  createdAt: string;
}

export interface AnonymousUserProfile {
  id: string;
  balances: Partial<Record<BalanceType, number>>;
  savedSongsCount: number;
  createdAt: string;
}

export type AnyUserProfile = UserProfile | AnonymousUserProfile;

export interface RegisteredAnonymousUser {
  id: string;
  balances: Partial<Record<BalanceType, number>>;
}

export interface RegisterAnonymousResponse {
  user: RegisteredAnonymousUser;
  token: string;
}

export interface RegisterRequest {
  login: string;
  password: string;
  name: string;
}

export interface RegisteredUser {
  id: string;
  login: string;
  name: string;
  balances: Partial<Record<BalanceType, number>>;
}

export interface RegisterResponse {
  user: RegisteredUser;
  balances: Partial<Record<BalanceType, number>>;
  token: string;
}

export interface LogInRequest {
  login: string;
  password: string;
}

export interface LoggedUser extends RegisteredUser {}

export interface LogInResponse {
  user: LoggedUser;
  token: string;
}

export interface LogOutResponse {
  success: boolean;
}

export interface AnonymousRecoveryCode {
  code: string;
}

export interface RecoveryFromCodeRequest {
  code: string;
}

export interface RecoveryFromCodeResponse {
  token: string;
}

// --------------------------------------------------------
// Songs / Tracks
// --------------------------------------------------------

export interface Track {
  trackId: string;
  previewUrl: string;
  downloadUrl: string | null;
}

/** The user-authored "preset" that a song was generated from. */
export type VocalPreset =
  | { type: 'MODEL'; gender: Gender }
  | { type: 'HUMAN'; voiceId: string };

export interface SongPreset {
  title: string;
  description: string | null;
  theme: string | null;
  lyrics: string;
  style: string;
  vocal: VocalPreset;
}

export interface Song {
  id: string;
  preset: SongPreset;
  isUnlocked: boolean;
  createdAt: string;
  tracks: Track[];
}

export interface ListSongsResponse {
  songs: Song[];
}

export interface ListSongsParams {
  /** Sent as repeated `statuses` query params: ?statuses=READY&statuses=GENERATING */
  statuses: readonly SongStatus[] | null;
  order?: SortingOrder | null;
  field?: 'created_at' | null;
  limit?: number | null;
  offset?: number | null;
}

export interface GenerateLyricsRequest {
  title: string;
  description: string | null;
}

export interface GenerateLyricsResponse {
  title: string;
  lyrics: string;
}

export interface PlaceSongGenerationRequest {
  preset: SongPreset;
}

export interface PlaceSongGenerationResponse {
  songId: string;
}

export interface PlaceSongGenerationResult {
  songId: string;
}

export interface Eta {
  /** ISO-8601 duration, e.g. "PT45S" */
  p50: string;
  /** ISO-8601 duration, e.g. "PT2M" */
  p90: string;
}

interface RawQueuedGenerationEvent {
  type: 'queued';
  songId: string;
  status: 'NEW';
}

interface RawAwaitingGenerationEvent {
  type: 'awaiting_generation';
  songId: string;
  status: 'AWAITING_GENERATION';
  eta: Eta;
}

interface RawGeneratingEvent {
  type: 'generating';
  songId: string;
  status: 'GENERATING';
}

interface RawFinishedEvent {
  type: 'ready';
  songId: string;
  status: 'READY';
  tracks: Track[];
  isUnlocked: boolean;
  generationsBalance: number;
}

interface RawFailedEvent {
  type: 'failed';
  songId: string;
  status: 'FAILED' | 'READY';
}

type RawGenerationEvent =
  | RawQueuedGenerationEvent
  | RawAwaitingGenerationEvent
  | RawGeneratingEvent
  | RawFinishedEvent
  | RawFailedEvent;

export type SongGenerationEvent =
  | { type: 'queued'; songId: string }
  | { type: 'awaiting_generation'; songId: string; eta: Eta }
  | {
      type: 'generating';
      songId: string;
      status: 'GENERATING';
    }
  | {
      type: 'finished';
      songId: string;
      tracks: Track[];
      isUnlocked: boolean;
      generationsBalance: number;
    }
  | { type: 'failed'; songId: string };

function normalizeGenerationEvent(
  raw: RawGenerationEvent,
): SongGenerationEvent {
  switch (raw.type) {
    case 'queued':
      return { type: 'queued', songId: raw.songId };
    case 'awaiting_generation':
      return { type: 'awaiting_generation', songId: raw.songId, eta: raw.eta };
    case 'generating':
      return { type: 'generating', songId: raw.songId, status: raw.status };
    case 'ready':
      return {
        type: 'finished',
        songId: raw.songId,
        tracks: raw.tracks,
        isUnlocked: raw.isUnlocked,
        generationsBalance: raw.generationsBalance,
      };
    case 'failed':
      return { type: 'failed', songId: raw.songId };
    default: {
      const unknown = raw as { type?: string };
      throw new Error(
        `Unknown generation event type: ${unknown.type ?? 'unknown'}`,
      );
    }
  }
}

export interface GenerationWatchHandlers {
  onQueued?: () => void;
  onAwaiting?: (eta: Eta) => void;
  onGenerating?: (status: 'GENERATING') => void;
  onFinished?: (tracks: Track[], generationsBalance: number) => void;
  onFailed?: () => void;
  /** Called once if the watcher had to fall back from SSE to plain polling. */
  onFallbackToPolling?: () => void;
}

export interface GenerationWatchHandle {
  stop: () => void;
}

// --------------------------------------------------------
// Payments
// --------------------------------------------------------

/**
 * Where the user was in the app when they initiated the payment.
 * The backend echoes this back on GET /payments/{id} so the frontend
 * knows where to send the user after they return from the payment page.
 */
export type PaymentContextSourceType = 'PROFILE' | 'MY_SONGS' | 'ROOT';

export interface PaymentContext {
  source: PaymentContextSourceType;
}

export type PaymentStatus =
  | 'NEW'
  | 'PENDING'
  | 'CONFIRMED'
  | 'FAILED'
  | 'CANCELLED';

export type PaymentPurposeType = 'PURCHASE' | 'TRACK_UNLOCK';

export interface PaymentPurpose {
  referenceId: string;
  type: PaymentPurposeType;
}

export type Currency = 'RUB';

export interface Money {
  amount: string;
  currency: Currency;
}

export interface Payment {
  id: string;
  ownerId: string;
  purpose: PaymentPurpose;
  context: PaymentContext | null;
  amount: Money;
  status: PaymentStatus;
  method: PaymentMethod;
  externalId: string | null;
  createdAt: string;
  updatedAt: string | null;
}

// --------------------------------------------------------
// Purchases
// --------------------------------------------------------

export interface PurchaseGenerationRequest {
  tariffId: string;
  context?: PaymentContext | null;
}

export interface PurchaseGenerationResponse {
  paymentUrl: string;
  paymentId: string;
}

export interface PurchaseTrackFromBalanceRequest {
  source: 'balance';
  context?: PaymentContext | null;
}

export interface PurchaseTrackFromPaymentRequest {
  source: 'payment';
  method: PaymentMethod;
  context?: PaymentContext | null;
}

export type PurchaseTrackRequest =
  | PurchaseTrackFromBalanceRequest
  | PurchaseTrackFromPaymentRequest;

export interface PurchaseTrackFromBalanceResponse {
  success: boolean;
}

export interface PurchaseTrackFromPaymentResponse {
  success: boolean;
  paymentUrl: string;
  paymentId: string;
}

export type PurchaseTrackResponse =
  | PurchaseTrackFromBalanceResponse
  | PurchaseTrackFromPaymentResponse;

export function isPurchaseTrackFromPayment(
  response: PurchaseTrackResponse,
): response is PurchaseTrackFromPaymentResponse {
  return 'paymentUrl' in response;
}

// --------------------------------------------------------
// Tariffs / Voice
// --------------------------------------------------------

/** Tariff item kinds supported by the backend. */
export type TariffItemType =
  | 'GENERATION'
  | 'GENERATION_VOCAL'
  | 'TRACK'
  | 'ADDITIONAL_GENERATION';

export interface GradientStopPosition {
  color: string;
  value: number;
}
export interface LinearGradient {
  angle: number;
  stops: GradientStopPosition[];
}
export interface TariffAppearance {
  background: string;
  border: string;
  buttonGradient: LinearGradient;
}
export interface TariffItem {
  amount: number;
  type: TariffItemType;
}
export interface Tariff {
  id: string;
  name: string;
  shortDescription: string;
  appearance: TariffAppearance;
  items: TariffItem[];
  price: Money;
  createdAt: string;
}
export interface PaginatedTariffs {
  tariffs: Tariff[];
  total: number;
}
export interface ListTariffsParams {
  order?: SortingOrder | null;
  field?: 'price' | 'id' | null;
  limit?: number | null;
  offset?: number | null;
  include_items?: readonly TariffItemType[] | null;
  exclude_items?: readonly TariffItemType[] | null;
}

export type AudioFormat = 'mp3' | 'wav';
export interface VoiceCapabilities {
  minDuration: number;
  maxDuration: number;
  allowedFormats: AudioFormat[];
  maxSizeBytes: number;
}
export interface CreatedVoice {
  voiceId: string;
}
export interface Voice {
  id: string;
  ownerId: string;
  status: VoiceStatus;
  createdAt: string;
  readyAt: string | null;
  failedAt: string | null;
}
export type VoiceStatus =
  | 'NEW'
  | 'AWAITING_DISPATCHING'
  | 'DISPATCHING'
  | 'DISPATCHED'
  | 'FAILED'
  | 'READY';
export type VoiceGenerationEvent =
  | {
      type: 'queued';
      voiceId: string;
      status: 'AWAITING_DISPATCHING' | 'DISPATCHING';
    }
  | { type: 'preparing'; voiceId: string; status: 'DISPATCHED' }
  | { type: 'ready'; voiceId: string; status: 'READY' }
  | { type: 'failed'; voiceId: string; status: 'FAILED' };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeVoiceGenerationEvent(value: unknown): VoiceGenerationEvent {
  if (!isRecord(value) || typeof value.type !== 'string') {
    throw new Error('Invalid voice generation event');
  }

  if (
    typeof value.voiceId !== 'string' ||
    typeof value.status !== 'string'
  ) {
    throw new Error('Invalid voice generation event payload');
  }

  switch (value.type) {
    case 'queued':
      if (
        value.status !== 'AWAITING_DISPATCHING' &&
        value.status !== 'DISPATCHING'
      ) {
        throw new Error('Invalid queued voice generation event status');
      }
      return { type: 'queued', voiceId: value.voiceId, status: value.status };
    case 'preparing':
      if (value.status !== 'DISPATCHED') {
        throw new Error('Invalid preparing voice generation event status');
      }
      return { type: 'preparing', voiceId: value.voiceId, status: value.status };
    case 'ready':
      if (value.status !== 'READY') {
        throw new Error('Invalid ready voice generation event status');
      }
      return { type: 'ready', voiceId: value.voiceId, status: value.status };
    case 'failed':
      if (value.status !== 'FAILED') {
        throw new Error('Invalid failed voice generation event status');
      }
      return { type: 'failed', voiceId: value.voiceId, status: value.status };
    default:
      throw new Error(`Unknown voice generation event type: ${value.type}`);
  }
}

export interface VoiceWatchHandlers {
  onQueued?: () => void;
  onPreparing?: () => void;
  onReady?: () => void;
  onFailed?: () => void;
  onFallbackToPolling?: () => void;
}
export interface ArchiveSongTracksRequest {
  songId: string;
}

export interface ArchiveSongTracksResponse {
  downloadUrl: string;
}

// --------------------------------------------------------
// Errors
// --------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ErrorResponse,
  ) {
    const message = body.message || 'Unknown API Error';
    super(`API error ${status}: ${message}`);
    this.name = 'ApiError';

    // Set prototype explicitly for correct instanceof behavior in ES5/ES6 environments
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// --------------------------------------------------------
// Client Config & Options
// --------------------------------------------------------

export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => string | null | undefined;
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

interface RequestOptions extends Omit<
  RequestInit,
  'method' | 'body' | 'signal'
> {
  method: HttpMethod;
  path: string;
  params?: Record<
    string,
    string | number | boolean | readonly string[] | null | undefined
  >;
  body?: unknown;
  skipAuth?: boolean;
  signal?: AbortSignal | null | undefined;
}

// --------------------------------------------------------
// Global Declarations for Vite (Prevents TS errors if vite/client is missing)
// --------------------------------------------------------

declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL?: string;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

const API_PREFIX = '/api/v1';

// Polling fallback interval when SSE is unavailable/interrupted.
const POLL_INTERVAL_MS = 3000;
// If SSE hasn't produced a single event within this window, fall back to polling.
const SSE_CONNECT_TIMEOUT_MS = 6000;

// --------------------------------------------------------
// API Client
// --------------------------------------------------------

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null | undefined;
  private readonly onUnauthorized: (() => void) | undefined;
  private readonly _fetch: typeof fetch;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.getToken = config.getToken ?? (() => null);
    this.onUnauthorized = config.onUnauthorized;
    this._fetch = config.fetch ?? globalThis.fetch.bind(globalThis);
  }

  private buildUrl(path: string, params?: RequestOptions['params']): string {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined) continue;
        if (Array.isArray(value)) {
          for (const item of value) url.searchParams.append(key, item);
          continue;
        }
        url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }

  private getHeaders(skipAuth?: boolean): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    if (!skipAuth) {
      const token = this.getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  private async parseErrorBody(res: Response): Promise<ErrorResponse> {
    try {
      const body = (await res.clone().json()) as Partial<ErrorResponse> & {
        error?: string;
      };
      if (typeof body.message === 'string') {
        return {
          message: body.message,
          trace_id: body.trace_id ?? null,
          detail: body.detail ?? null,
        };
      }
      if (typeof body.error === 'string') {
        return { message: body.error, trace_id: null, detail: null };
      }
      return { message: res.statusText || String(res.status), trace_id: null, detail: null };
    } catch {
      return { message: res.statusText || String(res.status), trace_id: null, detail: null };
    }
  }

  private async multipartRequest<T>(
    path: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<T> {
    const headers = new Headers({ Accept: 'application/json' });
    const token = this.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const form = new FormData();
    form.append('file', file);
    const res = await this._fetch(this.buildUrl(path), {
      method: 'POST',
      headers,
      body: form,
      signal,
    });
    if (res.status === 401) this.onUnauthorized?.();
    if (!res.ok) throw new ApiError(res.status, await this.parseErrorBody(res));
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  public async request<T>(opts: RequestOptions): Promise<T> {
    const { method, path, params, body, skipAuth, signal, ...fetchOpts } = opts;
    const url = this.buildUrl(path, params);

    const init: RequestInit = {
      ...fetchOpts,
      method,
      headers: this.getHeaders(skipAuth),
    };

    if (body !== undefined) init.body = JSON.stringify(body);
    if (signal !== undefined) init.signal = signal;

    const res = await this._fetch(url, init);

    if (res.status === 401) {
      const isAuthEndpoint =
        path.startsWith(`${API_PREFIX}/account/login`) ||
        path.startsWith(`${API_PREFIX}/account/register`);
      if (!isAuthEndpoint) {
        this.onUnauthorized?.();
      }
    }

    if (!res.ok) {
      throw new ApiError(res.status, await this.parseErrorBody(res));
    }

    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return undefined as T;
    }

    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /**
   * Fetches the current generation status once (GET, no streaming).
   * This is both the polling-fallback primitive and what the app uses on
   * startup to figure out whether a generation left running before a
   * page refresh is still in progress.
   */
  public getSongGeneration(
    songId: string,
    signal?: AbortSignal,
  ): Promise<SongGenerationEvent> {
    return this.request<RawGenerationEvent>({
      method: 'GET',
      path: `${API_PREFIX}/songs/${encodeURIComponent(songId)}/events`,
      signal,
    }).then(normalizeGenerationEvent);
  }

  public watchSongGeneration(
    songId: string,
    handlers: GenerationWatchHandlers,
    signal?: AbortSignal,
  ): GenerationWatchHandle {
    let stopped = false;
    let usingPolling = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let connectTimer: ReturnType<typeof setTimeout> | null = null;
    let es: EventSource | null = null;

    const clearTimers = () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
      if (connectTimer) {
        clearTimeout(connectTimer);
        connectTimer = null;
      }
    };

    const stop = () => {
      if (stopped) return;
      stopped = true;
      clearTimers();
      es?.close();
      es = null;
      signal?.removeEventListener('abort', stop);
    };

    const dispatch = (event: SongGenerationEvent) => {
      if (stopped) return;
      switch (event.type) {
        case 'queued':
          handlers.onQueued?.();
          break;
        case 'awaiting_generation':
          handlers.onAwaiting?.(event.eta);
          break;
        case 'generating':
          handlers.onGenerating?.(event.status);
          break;
        case 'finished':
          handlers.onFinished?.(event.tracks, event.generationsBalance);
          stop();
          break;
        case 'failed':
          handlers.onFailed?.();
          stop();
          break;
      }
    };

    const pollOnce = async () => {
      if (stopped) return;
      try {
        const event = await this.getSongGeneration(songId, signal);
        dispatch(event);
      } catch {
        // Transient network hiccup - keep retrying rather than giving up.
      }
      if (!stopped) pollTimer = setTimeout(pollOnce, POLL_INTERVAL_MS);
    };

    const startPolling = () => {
      if (usingPolling || stopped) return;
      usingPolling = true;
      clearTimers();
      es?.close();
      es = null;
      handlers.onFallbackToPolling?.();
      pollOnce();
    };

    const startSse = () => {
      const token = this.getToken();
      const url = new URL(
        `${this.baseUrl}${API_PREFIX}/songs/${encodeURIComponent(songId)}/events/sse`,
      );
      if (token) url.searchParams.set('token', token);

      try {
        es = new EventSource(url.toString());
      } catch {
        startPolling();
        return;
      }

      connectTimer = setTimeout(() => {
        if (!usingPolling) startPolling();
      }, SSE_CONNECT_TIMEOUT_MS);

      const handleMessage = (msg: Event) => {
        if (connectTimer) {
          clearTimeout(connectTimer);
          connectTimer = null;
        }

        const messageEvent = msg as MessageEvent;
        if (typeof messageEvent.data !== 'string') return;

        try {
          const raw = JSON.parse(messageEvent.data) as RawGenerationEvent;
          dispatch(normalizeGenerationEvent(raw));
        } catch (err) {
          console.error('Failed to parse SSE message', err);
        }
      };

      const eventTypes = [
        'queued',
        'awaiting_generation',
        'generating',
        'ready',
        'failed',
      ];
      eventTypes.forEach((eventType) => {
        es!.addEventListener(eventType, handleMessage);
      });

      es.onmessage = handleMessage;

      es.addEventListener('error', (event: Event) => {
        if (
          'data' in event &&
          typeof (event as MessageEvent).data === 'string'
        ) {
          try {
            const errorData = JSON.parse((event as MessageEvent).data);
            console.error('Server-sent SSE error:', errorData);
          } catch (err) {
            console.warn('Failed to parse server-sent SSE error:', err);
          }

          dispatch({ type: 'failed', songId });
          stop();
          return;
        }

        if (stopped || usingPolling) return;
        startPolling();
      });
    };

    startSse();
    signal?.addEventListener('abort', stop, { once: true });

    return { stop };
  }

  // --------------------------------------------------------
  // API Namespaces
  // --------------------------------------------------------

  public readonly user = {
    getProfile: (signal?: AbortSignal): Promise<AnyUserProfile> =>
      this.request<AnyUserProfile>({
        method: 'GET',
        path: `${API_PREFIX}/users/me`,
        signal,
      }),
  };

  public readonly account = {
    registerAnonymous: (
      signal?: AbortSignal,
    ): Promise<RegisterAnonymousResponse> =>
      this.request<RegisterAnonymousResponse>({
        method: 'POST',
        path: `${API_PREFIX}/account-anonymous/register`,
        skipAuth: true,
        signal,
      }),

    issueRecoveryCode: (signal?: AbortSignal): Promise<AnonymousRecoveryCode> =>
      this.request<AnonymousRecoveryCode>({
        method: 'POST',
        path: `${API_PREFIX}/account-anonymous/recovery-code`,
        signal,
      }),

    recoverAnonymous: (
      body: RecoveryFromCodeRequest,
      signal?: AbortSignal,
    ): Promise<RecoveryFromCodeResponse> =>
      this.request<RecoveryFromCodeResponse>({
        method: 'POST',
        path: `${API_PREFIX}/account-anonymous/recovery`,
        body,
        skipAuth: true,
        signal,
      }),

    register: (
      body: RegisterRequest,
      signal?: AbortSignal,
    ): Promise<RegisterResponse> =>
      this.request<RegisterResponse>({
        method: 'POST',
        path: `${API_PREFIX}/account/register`,
        body,
        signal,
      }),

    logIn: (body: LogInRequest, signal?: AbortSignal): Promise<LogInResponse> =>
      this.request<LogInResponse>({
        method: 'POST',
        path: `${API_PREFIX}/account/login`,
        body,
        signal,
      }),

    logOut: (signal?: AbortSignal): Promise<LogOutResponse> =>
      this.request<LogOutResponse>({
        method: 'POST',
        path: `${API_PREFIX}/account/logout`,
        signal,
      }),
  };

  public readonly songs = {
    list: (
      params?: ListSongsParams,
      signal?: AbortSignal,
    ): Promise<ListSongsResponse> =>
      this.request<ListSongsResponse>({
        method: 'GET',
        path: `${API_PREFIX}/songs/`,
        params: params as unknown as RequestOptions['params'],
        signal,
      }),

    delete: (songId: string, signal?: AbortSignal): Promise<SuccessResponse> =>
      this.request<SuccessResponse>({
        method: 'DELETE',
        path: `${API_PREFIX}/songs/${encodeURIComponent(songId)}`,
        signal,
      }),
    getCompressedTracks: (
      songId: string,
      signal?: AbortSignal,
    ): Promise<ArchiveSongTracksResponse> =>
      this.request<ArchiveSongTracksResponse>({
        method: 'POST',
        path: `${API_PREFIX}/songs/${encodeURIComponent(songId)}/tracks/archive`,
        body: { songId },
        signal,
      }),

    purchase: (
      songId: string,
      body:
        | PurchaseTrackFromBalanceRequest
        | Omit<PurchaseTrackFromPaymentRequest, 'method'>,
      signal?: AbortSignal,
    ): Promise<PurchaseTrackResponse> => {
      const payload: PurchaseTrackRequest =
        body.source === 'payment' ? { ...body, method: 'YOOKASSA' } : body;

      return this.request<PurchaseTrackResponse>({
        method: 'POST',
        path: `${API_PREFIX}/songs/${encodeURIComponent(songId)}/tracks/purchase`,
        body: payload,
        signal,
      });
    },

    generateLyrics: (
      body: GenerateLyricsRequest,
      signal?: AbortSignal,
    ): Promise<GenerateLyricsResponse> =>
      this.request<GenerateLyricsResponse>({
        method: 'POST',
        path: `${API_PREFIX}/songs/lyrics`,
        body,
        signal,
      }),

    placeSong: async (
      body: PlaceSongGenerationRequest,
      signal?: AbortSignal,
    ): Promise<PlaceSongGenerationResult> => {
      const response = await this.request<PlaceSongGenerationResponse>({
        method: 'POST',
        path: `${API_PREFIX}/songs/`,
        body,
        signal,
      });
      return { songId: response.songId };
    },

    placeAdditionalGeneration: (
      songId: string,
      signal?: AbortSignal,
    ): Promise<PlaceSongGenerationResponse> =>
      this.request<PlaceSongGenerationResponse>({
        method: 'POST',
        path: `${API_PREFIX}/songs/${encodeURIComponent(songId)}/generations`,
        signal,
      }),

    getGeneration: (
      songId: string,
      signal?: AbortSignal,
    ): Promise<SongGenerationEvent> => this.getSongGeneration(songId, signal),

    watchGeneration: (
      songId: string,
      handlers: GenerationWatchHandlers,
      signal?: AbortSignal,
    ): GenerationWatchHandle =>
      this.watchSongGeneration(songId, handlers, signal),
  };

  public readonly tariffs = {
    list: (
      params?: ListTariffsParams,
      signal?: AbortSignal,
    ): Promise<PaginatedTariffs> =>
      this.request<PaginatedTariffs>({
        method: 'GET',
        path: `${API_PREFIX}/tariffs/`,
        params: params as unknown as RequestOptions['params'],
        signal,
      }),
  };

  public readonly voices = {
    get: (voiceId: string, signal?: AbortSignal): Promise<Voice> =>
      this.request<Voice>({
        method: 'GET',
        path: `${API_PREFIX}/voices/${encodeURIComponent(voiceId)}`,
        signal,
      }),

    capabilities: (signal?: AbortSignal): Promise<VoiceCapabilities> =>
      this.request<VoiceCapabilities>({
        method: 'GET',
        path: `${API_PREFIX}/voices/capabilities`,
        signal,
      }),
    validate: (file: File, signal?: AbortSignal): Promise<Violations> =>
      this.multipartRequest<Violations>(
        `${API_PREFIX}/voices/validate`,
        file,
        signal,
      ),
    create: (file: File, signal?: AbortSignal): Promise<CreatedVoice> =>
      this.multipartRequest<CreatedVoice>(
        `${API_PREFIX}/voices/`,
        file,
        signal,
      ),
    prepare: (
      voiceId: string,
      signal?: AbortSignal,
    ): Promise<SuccessResponse> =>
      this.request<SuccessResponse>({
        method: 'POST',
        path: `${API_PREFIX}/voices/prepare`,
        body: { voiceId },
        signal,
      }),
    getGeneration: (
      voiceId: string,
      signal?: AbortSignal,
    ): Promise<VoiceGenerationEvent> =>
      this.request<unknown>({
        method: 'GET',
        path: `${API_PREFIX}/voices/${encodeURIComponent(voiceId)}/events`,
        signal,
      }).then(normalizeVoiceGenerationEvent),
    watchGeneration: (
      voiceId: string,
      handlers: VoiceWatchHandlers,
      signal?: AbortSignal,
    ): GenerationWatchHandle => {
      let stopped = false,
        polling = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let connectTimer: ReturnType<typeof setTimeout> | null = null;
      let es: EventSource | null = null;
      const seen = new Set<string>();
      const stop = () => {
        if (stopped) return;
        stopped = true;
        if (timer) clearTimeout(timer);
        if (connectTimer) clearTimeout(connectTimer);
        es?.close();
        signal?.removeEventListener('abort', stop);
      };
      const dispatch = (raw: unknown): void => {
        if (!isRecord(raw)) return;
        const type = typeof raw.type === 'string' ? raw.type : 'unknown';
        const status = typeof raw.status === 'string' ? raw.status : '';
        const rawVoiceId = typeof raw.voiceId === 'string' ? raw.voiceId : voiceId;
        const key = `${type}:${status}:${rawVoiceId}`;
        if (seen.has(key) || stopped) return;
        seen.add(key);
        if (type === 'queued') handlers.onQueued?.();
        else if (type === 'preparing') handlers.onPreparing?.();
        else if (type === 'ready') {
          handlers.onReady?.();
          stop();
        } else if (type === 'failed') {
          handlers.onFailed?.();
          stop();
        }
      };
      const poll = async () => {
        if (stopped) return;
        try {
          const raw = await this.request<unknown>({
            method: 'GET',
            path: `${API_PREFIX}/voices/${encodeURIComponent(voiceId)}/events`,
            signal,
          });
          dispatch(raw);
        } catch {}
        if (!stopped) timer = setTimeout(poll, POLL_INTERVAL_MS);
      };
      const startPolling = () => {
        if (polling || stopped) return;
        polling = true;
        es?.close();
        handlers.onFallbackToPolling?.();
        poll();
      };
      try {
        const url = new URL(
          `${this.baseUrl}${API_PREFIX}/voices/${encodeURIComponent(voiceId)}/events/sse`,
        );
        const token = this.getToken();
        if (token) url.searchParams.set('token', token);
        es = new EventSource(url.toString());
        connectTimer = setTimeout(startPolling, SSE_CONNECT_TIMEOUT_MS);
        const message = (event: Event) => {
          if (connectTimer) {
            clearTimeout(connectTimer);
            connectTimer = null;
          }
          const data = (event as MessageEvent).data;
          if (typeof data !== 'string' || !data) return;
          try {
            dispatch(JSON.parse(data));
          } catch {}
        };
        ['queued', 'preparing', 'ready', 'failed', 'heartbeat'].forEach(
          (type) => es!.addEventListener(type, message),
        );
        es.onmessage = message;
        es.onerror = () => startPolling();
      } catch {
        startPolling();
      }
      signal?.addEventListener('abort', stop, { once: true });
      return { stop };
    },
  };

  public readonly payments = {
    purchaseGeneration: (
      body?: PurchaseGenerationRequest,
      signal?: AbortSignal,
    ): Promise<PurchaseGenerationResponse> =>
      this.request<PurchaseGenerationResponse>({
        method: 'POST',
        path: `${API_PREFIX}/purchases/`,
        body: body ?? {},
        signal,
      }),

    get: (paymentId: string, signal?: AbortSignal): Promise<Payment> =>
      this.request<Payment>({
        method: 'GET',
        path: `${API_PREFIX}/payments/${encodeURIComponent(paymentId)}`,
        signal,
      }),
  };

  public readonly health = {
    check: (signal?: AbortSignal): Promise<SuccessResponse> =>
      this.request<SuccessResponse>({
        method: 'GET',
        path: `${API_PREFIX}/health/`,
        signal,
      }),
  };
}

const API_BASE_URL =
  import.meta.env?.VITE_API_BASE_URL ?? 'http://localhost:8000';

export const client = new ApiClient({
  baseUrl: API_BASE_URL,
  getToken: () =>
    localStorage.getItem('token') || localStorage.getItem('anonymous_token'),
  onUnauthorized: () => {
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  },
});
